import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { $isListNode, INSERT_UNORDERED_LIST_COMMAND, ListNode, REMOVE_LIST_COMMAND } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createQuoteNode, $isHeadingNode } from '@lexical/rich-text'
import { $isAtNodeEnd, $setBlocksType } from '@lexical/selection'
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND } from 'lexical'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ReactComponent as BoldIcon } from '@/assets/icons/toolbar/bold.svg'
import { ReactComponent as BulletIcon } from '@/assets/icons/toolbar/bullet.svg'
import { ReactComponent as CodeIcon } from '@/assets/icons/toolbar/code.svg'
import { ReactComponent as ItalicIcon } from '@/assets/icons/toolbar/italic.svg'
import { ReactComponent as LinkIcon } from '@/assets/icons/toolbar/link.svg'
import { ReactComponent as QuoteIcon } from '@/assets/icons/toolbar/quote.svg'
import { ReactComponent as StrikethroughIcon } from '@/assets/icons/toolbar/strikethrough.svg'
import { ReactComponent as UnderlineIcon } from '@/assets/icons/toolbar/underline.svg'

import { StyledToolbar } from './Editor.styles'
import { FloatingLinkEditor } from './FloatingLinkEditor'

const LowPriority = 1

export function Toolbar() {
  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef(null)
  const [blockType, setBlockType] = useState('paragraph')
  const [isLink, setIsLink] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isCode, setIsCode] = useState(false)

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode)
          const type = parentList ? parentList.getTag() : element.getTag()
          setBlockType(type)
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType()
          setBlockType(type)
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsCode(selection.hasFormat('code'))

      // Update links
      const node = getSelectedNode(selection)
      const parent = node.getParent()
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }
    }
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          updateToolbar()
          return false
        },
        LowPriority,
      ),
    )
  }, [editor, updateToolbar])

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://')
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink])

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      })
    }
  }

  return (
    <StyledToolbar ref={toolbarRef}>
      <>
        <button
          aria-label="Format Bold"
          className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
          }}
        >
          <BoldIcon />
        </button>
        <button
          aria-label="Format Italics"
          className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
          }}
        >
          <ItalicIcon />
        </button>
        <button
          aria-label="Format Underline"
          className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
          }}
        >
          <UnderlineIcon />
        </button>
        <button
          aria-label="Format Strikethrough"
          className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
          }}
        >
          <StrikethroughIcon />
        </button>
        <button
          aria-label="Insert Code"
          className={'toolbar-item spaced ' + (isCode ? 'active' : '')}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
          }}
        >
          <CodeIcon />
        </button>
        <button
          aria-label="Insert Link"
          className={'toolbar-item spaced mr-2 ' + (isLink ? 'active' : '')}
          onClick={insertLink}
        >
          <LinkIcon />
        </button>
        {isLink && createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
        <Divider />
        <button className={'toolbar-item spaced ' + (blockType === 'ul' ? 'active' : '')} onClick={formatBulletList}>
          <BulletIcon />
        </button>
        <button className={'toolbar-item spaced ' + (blockType === 'quote' ? 'active' : '')} onClick={formatQuote}>
          <QuoteIcon />
        </button>
      </>
    </StyledToolbar>
  )
}

export function getSelectedNode(selection) {
  const anchor = selection.anchor
  const focus = selection.focus
  const anchorNode = selection.anchor.getNode()
  const focusNode = selection.focus.getNode()
  if (anchorNode === focusNode) {
    return anchorNode
  }
  const isBackward = selection.isBackward()
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode
  }
}

function Divider() {
  return <div className="divider" />
}
